(ns pfo.task.chart
  (:require
   [clojure.string :as str]
   #_:clj-kondo/ignore [taoensso.timbre :as timbre
                        :refer [log trace debug info warn error fatal report logf
                                tracef debugf infof warnf errorf fatalf reportf spy]]
   [reagent.core :as reagent :refer [atom track!]]
   ["@nivo/bar" :refer [ResponsiveBar]]
   ["@nivo/core" :refer [useTheme sanitizeSvgTextStyle]]
   ["html-to-image" :refer [toPng]]
   ["pdf-lib" :as pdf-lib]
   ["@react-spring/web" :refer [animated]]
   [pfo.util :as util]
   [pfo.widget.icons :refer [padlock]]
   [pfo.task.model :as task-model]))

(def regular-bar-color "#008080")
(def selected-bar-color "#569e89")
(def hl-bar-color "#abe4d3")
(def overdue-bar-colors [;; Each age has the same color:
                         "#eb5757"
                         "#eb5757"
                         "#eb5757"
                         "#eb5757"
                         "#eb5757"
                         "#eb5757"
                         ;; "Shades of yellow":
                         ;; "#ede2c7"
                         ;; "#e4d3ab"
                         ;; "#dbc58f"
                         ;; "#d3b773"
                         ;; "#a8925c"
                         ;; "#8a7b63"
                         ])



(defn get-color [age mode]
  (let [age (util/clamp age 0 6)]
    (if (zero? age)
      (case mode
        :normal regular-bar-color
        :selected selected-bar-color
        :highlighted hl-bar-color)
      (let [color (nth overdue-bar-colors (dec age))]
        (case mode
          :normal color
          :selected (util/adjust-lightness color -25)
          :highlighted (util/adjust-lightness color 80))))))

(defn download-chart [chart-ref]
  (when chart-ref
    (-> (toPng chart-ref)
        (.then (fn [data-url]
                 (let [link (js/document.createElement "a")]
                   (set! (.-href link) data-url)
                   (set! (.-download link) "chart.png")
                   (.click link))))
        (.catch (fn [error] (js/console.error "Error generating image:" error))))))

(defn download-chart-as-pdf [chart-ref]
  (when chart-ref
    (-> (toPng chart-ref #js {:pixelRatio 2})
        (.then (fn [data-url]
                 (-> (.create pdf-lib/PDFDocument)
                     (.then (fn [pdf-doc]
                              (-> (.embedPng pdf-doc data-url)
                                  (.then (fn [image]
                                           (let [image-width (.-width image)
                                                 image-height (.-height image)]
                                             (if (and (number? image-width) (number? image-height))
                                               (let [page (.addPage pdf-doc #js [ image-width
                                                                                  image-height])]
                                                 (.drawImage page image {:x 0 :y 0
                                                                         :width image-width
                                                                         :height image-height})
                                                 (-> (.save pdf-doc)
                                                     (.then (fn [pdf-bytes]
                                                              (let [blob (js/Blob. (clj->js [pdf-bytes]) {:type "application/pdf"})
                                                                    link (js/document.createElement "a")]
                                                                (set! (.-href link) (.createObjectURL js/URL blob))
                                                                (set! (.-download link) "chart.pdf")
                                                                (.click link))))))
                                               (js/console.error "Error: Invalid image dimensions")))))))))))
        (.catch (fn [error] (js/console.error "Error generating PDF:" error))))))


(defn age-key-to-num [age-key]
  (util/str->int (last (str/split (str age-key) #"-"))))

(defn log-transform [x]
  (Math/log (if (zero? x) 1 (+ 1 (* 0.15 x)))))

(defn axis-tick
  [{:keys [value format lineX lineY onClick textBaseline textAnchor animatedProps selected date is-signature]}]
  (let [theme (js->clj (useTheme) :keywordize-keys true)
        line-style (get-in theme [:axis :ticks :line])
        text-style (get-in theme [:axis :ticks :text])
        text-style (if selected
                     (assoc text-style :fontWeight "bold")
                     text-style)
        formatted-value (or (and format (format value)) value)
        style {:opacity (.-opacity animatedProps)}
        props (if onClick
                {:style (merge style {:cursor "pointer"})
                 :on-click (fn [event] (onClick event formatted-value))}
                {:style style})]
    [:> (.-g animated)
     (merge
       {:transform (:transform animatedProps)}
       props)
     [:line {:x1 0
             :x2 lineX
             :y1 0
             :y2 lineY
             :style line-style}]
     (when (> (:outlineWidth text-style) 0)
       [:> (.-text animated)
        {:dominantBaseline textBaseline
         :textAnchor textAnchor
         :transform (:textTransform animatedProps)
         :style text-style
         :strokeWidth (* 2 (:outlineWidth text-style))
         :stroke (:outlineColor text-style)
         :strokeLinejoin "round"}
        (str formatted-value)])
     (when is-signature
       [:> (.-g animated) [padlock {:width 14 :height 14 :x -7 :y 7} false false]])
     [:> (.-text animated)
      {:dominantBaseline textBaseline
       :textAnchor textAnchor
       :transform (:textTransform animatedProps)
       :style (sanitizeSvgTextStyle (clj->js text-style))}
      [:> (.-tspan animated) {:x "0" :dy "1.2em"} formatted-value]
      [:> (.-tspan animated) {:x "0" :dy "1.2em"} date]]]))

(defn render-tick [selected-step-index index-date-map template-shape tick]
  (let [tick (js->clj tick :keywordize-keys true)
        is-signature (contains?
                       #{task-model/signature-type-cycle
                         task-model/signature-type-step
                         task-model/signature-type-document}
                       ;; not-found value specified as render-tick appears to be called at times with out of date tick value
                       (:signature_scope (nth (:task_templates template-shape) (:value tick) nil)))]
    (reagent/as-element
      [:f> axis-tick (assoc tick
                       :selected (= selected-step-index (:value tick))
                       :date (index-date-map (:value tick))
                       :is-signature is-signature
                       :value (str "Step " (inc (:value tick))))])))

(defn bar-chart [_ {:keys [data-atom]}]
  (let [hl-step (atom nil)
        step-index-data (track! (fn [] (util/associate-by :step_index (:chart_records @data-atom))))
        chart-elem (atom nil)]
    (fn [_ {:keys [selected-step-atom template-shape]}]
      (let [data (map (fn [index]
                        (if (contains? @step-index-data index)
                          (let [entry (@step-index-data index)]
                            {:step-index (:step_index entry)
                             :step-date (util/date-reformat (:step_date entry))
                             :age (reduce (fn [age-map [age num]]
                                            (if (<= age 6)
                                              (assoc age-map age num)
                                              (update age-map 6 (fnil inc 0))))
                                    {}
                                    (update-keys (:age entry) (comp util/str->int name) ))})
                          {:step-index index})) (range (count (:task_templates template-shape))))


            step-index-date-map (into {} (map (fn [step] [(:step-index step) (:step-date step)]) data))
            flat-data (map (fn [item]
                             (apply merge item
                               (map (fn [[age value]]
                                      {(keyword (str "age-" age)) (log-transform value)
                                       (keyword (str "age-label-" age)) value})
                                 (:age item))))
                        data)
            keys (distinct (flatten (map (fn [item]
                                           (map (fn [age] (keyword (str "age-" age))) (sort (keys (:age item))))) data)))]
        @selected-step-atom @hl-step ;; force rerender
        [:div.chart {:class :chart :style {:max-width "100%" :height "500px" :text-align "center"}
                     :ref (fn [elem] (reset! chart-elem elem))}
         [:> ResponsiveBar
          {:data         flat-data
           :keys         keys
           :indexBy      :step-index
           :margin       {:top 25 :right 25 :bottom 60 :left 25}
           :padding      0.3
           :valueScale   {:type "linear"}
           :indexScale   {:type "band" :round true}
           :theme {:labels {:text {:fontSize 14}}
                   :axis {:ticks {:text {:fontSize 14}}}}
           :colors (fn [bar]
                     (let [bar (js->clj bar :keywordize-keys true )
                           age (age-key-to-num (:id bar))]
                       (get-color age (condp = (get-in bar [:data :step-index] 0)
                                        @selected-step-atom :selected
                                        @hl-step :highlighted
                                        :normal))))
           :axisBottom   (clj->js {:tickSize 5 :tickPadding 5 :tickRotation 0 :legend nil :legendPosition "middle" :legendOffset 32
                                   :renderTick #(render-tick @selected-step-atom step-index-date-map template-shape %)})
           :axisLeft     {:tickSize 0 :tickPadding 5 :tickRotation 0 :legend "" :legendPosition "middle" :legendOffset -40 :format (constantly "")}
           :onClick (fn [bar]
                      (let [bar (js->clj bar :keywordize-keys true )]
                        (swap!
                          selected-step-atom
                          (fn [value index]
                            (if (= value index)
                              nil
                              index))
                          ;; For some reason, nivo passes bar data without
                          ;; :step-index when it's 0, hance nil to 0 conversion
                          (get-in bar [:data :step-index] 0))))
           :onMouseEnter (fn [bar event]
                           (aset (.-target event) "style" "cursor" "pointer")
                           (let [bar (js->clj bar :keywordize-keys true )]
                             (reset! hl-step (get-in bar [:data :step-index] 0))))
           :onMouseLeave (fn [_]
                           (reset! hl-step nil))
           :enableTooltip false
           :enableGridY false
           :tooltip (fn [bar] (let [bar (js->clj bar :keywordize-keys true )]
                                (task-model/strip-step-prefix (:name (nth (:task_templates template-shape) (get-in bar [:data :step-index] 0))))))
           :motionConfig {:default {:duration 200}}
           :label (fn [bar]
                    (let [bar (js->clj bar :keywordize-keys true )
                          age (age-key-to-num (:id bar))]
                      (get-in bar [:data (keyword (str "age-label-" age))])))
           :enableLabel  true
           :borderWidth 0.3
           :borderColor   {:from "color" :modifiers [["darker" 1.6]]} }]
         (when @chart-elem
           #_ [:button.o-flex-align--element-to-right.c-button--small.u-margin-top--xs
            {:style {:margin-right "calc(25px + 1.5em)"}
             :on-click (util/handler
                         (fn []
                           (download-chart @chart-elem)))} "PNG"] #_" "
           #_ [:button.o-flex-align--element-to-right.c-button--small.u-margin-top--xs
            {:style {:margin-right "calc(25px + 1.5em)"}
             :on-click (util/handler
                         (fn []
                           (download-chart-as-pdf @chart-elem)))} "PDF"])]))))
